<template>
  <div class="clearbit-logo">
    <img
      v-if="logoUrl && !error"
      :src="logoUrl"
      class="clearbit-logo__img"
      @load="onLoad"
      @error="onError"
    />
    <slot v-else />
  </div>
</template>

<script lang="ts" setup>
/**
 * This component renders the company logo from company url
 * When image loading fails, it also provide fallback options with slots.
 * https://clearbit.com/logo
 */
import type { PropType } from 'vue'
import { ref, computed, watch } from 'vue'
import { getClearbitLogo } from './getClearbitLogo'

const props = // eslint-disable-next-line vue/define-props-declaration
  defineProps({
    companyUrl: {
      type: String as PropType<string | null>,
      default: null,
    },
  })

const emit = defineEmits<{
  (e: 'error'): void
  (e: 'load', url: string): void
}>()

const error = ref(false)

const logoUrl = computed(() => getClearbitLogo(props.companyUrl))

watch(logoUrl, (currentValue) => {
  if (!currentValue) {
    error.value = true
    emit('error')
  } else {
    error.value = false
  }
})

const onError = (): void => {
  error.value = true
  emit('error')
}

const onLoad = (): void => {
  if (logoUrl.value) {
    error.value = false
    emit('load', logoUrl.value)
    return
  }
  onError()
}
</script>

<style lang="scss" scoped>
.clearbit-logo {
  @include row--center;
}

.clearbit-logo__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  overflow: hidden;
}
</style>
